/**
 * Assignments tab of the ONE Profile drawer.
 *
 * Hosts ONE's linked-students surface — the guardian's students from
 * `useMyStudents`, with loading / list / empty states. ONE's
 * "assignments" are its linked students; there is no site/duty concept here
 * (that part of the AIM tab has no ONE counterpart).
 */
import { Box, Center, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { HiShieldCheck } from 'react-icons/hi2';
import { useMyStudents } from '@/hooks/useReunification';
import { guardianStudentName } from '@/services/reunification';

export function ProfileAssignmentsTab() {
  const { t } = useTranslation();
  const { data: studentsData, isLoading: studentsLoading } = useMyStudents();

  const myStudents = studentsData?.results ?? [];

  return (
    <Box>
      <HStack gap={2} mb={2}>
        <HiShieldCheck size={18} />
        <Text fontSize="sm" fontWeight="600" color="text.primary">
          {t('profile.linkedStudents', { count: myStudents.length })}
        </Text>
      </HStack>

      {studentsLoading ? (
        <Center py={4}>
          <Spinner size="sm" />
        </Center>
      ) : myStudents.length > 0 ? (
        <VStack gap={2} align="stretch">
          {myStudents.map((student) => (
            <Box
              key={student.public_id}
              p={3}
              borderRadius="lg"
              border="1px solid"
              borderColor="border.secondary"
            >
              <Text fontSize="sm" fontWeight="600" color="text.primary">
                {guardianStudentName(student)}
              </Text>
              <Text fontSize="xs" color="text.secondary">
                {student.grade} {student.homeroom ? `— ${student.homeroom}` : ''}
              </Text>
            </Box>
          ))}
        </VStack>
      ) : (
        <Text fontSize="sm" color="text.secondary">
          {t('profile.noLinkedStudents')}
        </Text>
      )}
    </Box>
  );
}
